import { getElectronStores } from '../../stores/electronStores';
import { loginProfileState, machineNameState } from '../../stores/shared';
import * as remoteControl from '../../messaging/handlers/rcHost';
import { getIpcFunctions } from '../ipcInterface';
import { isElectron } from '../..';
import {
  createRemoteControlHost,
  getProfile,
  MakeHostPeerConfig,
  PlaycastPeerRemoteControlHost,
  PlaycastRemotePeer,
} from '@playcastdotio/Peer';
import { addReplicationStateChangeCallback, getNotifyMessages } from '@playcastdotio/StateStore';
import { PeerMessage, PlaycastMessage, PlaycastRemoteMessages } from '@playcastdotio/Messaging';
import { getTracksForProfile } from '../managers/mediaManager';
import { connect as remoteControlApiConnect, sendMessage } from '../remoteHostRealtime';
import { getStateStore } from '../../stores';
import { PlaycastChannels } from '@playcastdotio/CoreTypes';

// const HEARTBEAT_INTERVAL = 30 * 1000; // 30 seconds

let enabled = false;
let enabling = false;
// let remoteHostSessionId = '';
let remoteHost: PlaycastPeerRemoteControlHost | undefined;
let remoteClients: { [remoteId: string]: PlaycastRemotePeer } = {};
// let defaultTracks: Tracks = {};
const electronStores = getElectronStores();
const state = getStateStore('ipc');
export const enableRemoteControl = async () => {
  if (!isElectron) {
    console.error('[RC] Cannot enable remote control on non-host');
  }
  if (enabled) {
    console.warn('[RC] Remote control already enabled');
    return;
  }
  if (loginProfileState.currentState.accessToken === '' && loginProfileState.currentState.hostAuthId === '') {
    console.warn('[RC] Cannot enable remote control without being logged in.');
    return;
  }
  if (enabling) {
    console.warn('[RC] Remote control already enabling');
    return;
  }
  enabling = true;
  console.debug('[RC] Enabling remote control');
  remoteControlApiConnect();

  createRemoteHost();
};

export const createRemoteHost = async () => {
  console.log('[RC] Creating remote host');

  const remoteHostSessionId = await new Promise<string>((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error('Timeout waiting for peer ID'));
    }, 10000);

    if (state.hostPresenceState.currentState.remoteHostPeerId) {
      clearTimeout(timeout);
      resolve(state.hostPresenceState.currentState.remoteHostPeerId);
    }
    const listener = state.hostPresenceState.addListener((presence) => {
      if (presence.remoteHostPeerId) {
        clearTimeout(timeout);
        state.hostPresenceState.removeListener(listener);
        resolve(presence.remoteHostPeerId || '');
      }
    });
  });

  if (!remoteHostSessionId || remoteHostSessionId === '') {
    console.error('[RC] Failed to get remote host session id');
    enabling = false;
    enabled = false;
    return;
  }

  console.debug('[RC] Remote host session id: ', remoteHostSessionId);
  const ipcInterface = getIpcFunctions();
  const iceServers = state.iceServerState.currentState.remoteHostIceServers;

  remoteHost = createRemoteControlHost(
    {
      realtimeApiManager: {
        send: (message) => {
          sendMessage(message);
        },
      },
      iceServers: iceServers || undefined,
      forceTurn: !state.iceServerState.currentState.allowPeerConnections,
    },
    remoteHostSessionId,
  );
  // if (defaultTracks) {
  //   remoteHost.setDefaultTracks(defaultTracks);
  // }
  let remotePeerCache = remoteHost.remotePeers.currentState;
  remoteHost.remotePeers.addListener((newRemotePeers) => {
    if (remotePeerCache !== newRemotePeers) {
      const addedPeers = Object.keys(newRemotePeers).filter((peer) => !Object.keys(remotePeerCache).includes(peer));
      const removedPeers = Object.keys(remotePeerCache).filter((peer) => !Object.keys(newRemotePeers).includes(peer));
      if (removedPeers.length > 0) {
        console.debug('[RC] Removed remote connections:', removedPeers);
      }
      addedPeers.forEach((peer) => {
        const newPeer = newRemotePeers[peer];
        newPeer.onMessage((message) => {
          console.log('[RC] Message from', newPeer.getMetaData().peerId, message);
          if (message.header.target === 'remote') {
            remoteControl.handleIncomingMessages(message as PlaycastMessage<PlaycastRemoteMessages>, ipcInterface);
          } else {
            console.warn('[RC] Unhandled message target:', message.header.target);
          }
        });
        const notifyMessages = getNotifyMessages(['remoteClient']);
        notifyMessages.remoteClient.forEach((message) => {
          console.warn('[RC] Notify', message.body.message.channel);
          newPeer.sendMessage(message);
        });
        const peerState = newPeer.profileState;
        peerState.updateState({ profile: 'hostMedium' });
        const tracks = getTracksForProfile(getProfile(peerState.currentState.profile));
        newPeer.setTracks(tracks);
        peerState.addListener((state) => {
          const tracks = getTracksForProfile(getProfile(state.profile));
          newPeer.setTracks(tracks);
        });
      });
      remotePeerCache = newRemotePeers;
      remoteClients = newRemotePeers;
    }
  });

  addReplicationStateChangeCallback('remoteClient', (message) => {
    remoteHost?.broadcastMessage(message);
  });

  remoteHost.status.addListener((status) => {
    // console.log('[RC] Remote control host status:', status);
    if (status.connectionState === 'listening') {
      enabled = true;
      enabling = false;
    }
    if (status.connectionState === 'disconnected' || status.connectionState === 'failed' || status.connectionState === 'closed') {
      remoteHost?.close('rc:close:status_disconnected');
      enabled = false;
      enabling = false;
    }
  });
};

export const remoteHostHandlePeerMessage = (message: PeerMessage) => {
  if (!remoteHost) {
    console.error('[RC] Remote host not initialized');
    return;
  } else {
    remoteHost.handlePeerMessage(message);
  }
};

// can just access the state store instead of doing the call to this
export const getRemoteControlId = () => {
  return electronStores.remoteHostState.currentState.remoteSessionId;
};

export const disableRemoteControl = () => {
  Object.values(remoteClients).forEach((peer) => {
    peer.disconnect();
  });
  remoteClients = {};
  enabled = false;
  enabling = false;
  // reset the remoteHost state store
  electronStores.remoteHostState.reset();
};

export const notifyTracksChanged = () => {
  console.warn('[RC] Notifying tracks changed');
  // const profile = getProfile('hostHigh');
  // const tracks = getTracksForProfile(profile);

  // for each remote host peer, send the tracks
  Object.values(remoteClients).forEach((peer) => {
    const tracks = getTracksForProfile(getProfile(peer.profileState.currentState.profile));
    peer.setTracks(tracks);
    console.log('[RC] Sending tracks to remote clients', tracks);
  });
};

// let previousTracks: string[] = [];

// export const setTracks = (tracks: Tracks) => {
//   const newTracks = Object.keys(tracks);
//   const changedTracks = newTracks.filter((track) => !previousTracks.includes(track));
//   if (changedTracks.length > 0) {
//     console.error('[RC] Setting tracks', tracks);
//     Object.values(remoteClients).forEach((peer) => {
//       peer.setTracks(tracks);
//     });
//     remoteHost?.setDefaultTracks(tracks);
//     defaultTracks = tracks;
//   }
//   previousTracks = newTracks;
// };

export const enableTracksByChannel = (channel: PlaycastChannels, enable: boolean, peerId?: string) => {
  const tracks = remoteHost?.defaultTracks.currentState;
  if (!tracks) {
    console.error('[RC] No tracks to enable/disable');
    return;
  }
  const trackIds = Object.keys(tracks).filter((trackId) => tracks[trackId].metadata.channel === channel);
  const filteredTracks = trackIds.map((trackId) => {
    return {
      track: tracks[trackId].track,
      metadata: tracks[trackId].metadata,
    };
  });
  filteredTracks.forEach((track) => {
    if (enable) {
      console.warn('[RC] Enabling track', track.metadata.displayName);
      track.track.enabled = true;
    } else {
      console.warn('[RC] Disabling track', track.metadata.displayName);
      track.track.enabled = false;
    }
  });
};

export const updateConfig = (config: Partial<MakeHostPeerConfig>) => {
  if (!remoteHost) {
    console.warn('[RC] No remote host, cannot update config');
    return;
  }
  remoteHost.updateConfig(config);
};
